import { useApiGet } from 'hooks/useApi';
import Pagination from 'models/Pagination';
import Customer from 'models/Customer';
import License from 'models/License';
import AdminUser from 'models/AdminUser';
import AdminNotification from 'models/AdminNotification';
import { AdminExternalNewsChannel } from 'models/AdminExternalNewsChannel';
import { AdminExternalNewsCategory } from 'models/AdminExternalNewsCategory';
import { AdminSupportContact } from 'models/AdminSupportContact';

export interface AdminDashboardStats {
  customers: {
    total: number;
  };
  licenses: {
    total: number;
    active: number;
    inUse: number;
    available: number;
  };
  adminUsers: {
    total: number;
  };
  notifications: {
    total: number;
  };
  newsChannels: {
    total: number;
  };
  newsCategories: {
    total: number;
  };
  supportContacts: {
    total: number;
  };
}

const useControllerDashboardAdmin = () => {
  // Fetch customers
  const { data: customersData, isLoading: isLoadingCustomers } = useApiGet<
    Pagination<Customer>
  >('/admin/customers', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch licenses
  const { data: licensesData, isLoading: isLoadingLicenses } = useApiGet<
    Pagination<License>
  >('/admin/licenses', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch admin users
  const { data: adminUsersData, isLoading: isLoadingAdminUsers } = useApiGet<
    Pagination<AdminUser>
  >('/admin/auth/users', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch notifications
  const {
    data: notificationsData,
    isLoading: isLoadingNotifications
  } = useApiGet<Pagination<AdminNotification>>('/admin/notifications', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch news channels
  const {
    data: newsChannelsData,
    isLoading: isLoadingNewsChannels
  } = useApiGet<Pagination<AdminExternalNewsChannel>>('/admin/news/channels', {
    params: { pageSize: 1000, page: 1 }
  });

  // Fetch news categories
  const {
    data: newsCategoriesData,
    isLoading: isLoadingNewsCategories
  } = useApiGet<Pagination<AdminExternalNewsCategory>>(
    '/admin/news/categories',
    {
      params: { pageSize: 1000, page: 1 }
    }
  );

  // Fetch support contacts
  const {
    data: supportContactsData,
    isLoading: isLoadingSupportContacts
  } = useApiGet<Pagination<AdminSupportContact>>('/admin/support/contacts', {
    params: { pageSize: 1000, page: 1 }
  });

  const customers = customersData?.items || [];
  const licenses = licensesData?.items || [];
  const adminUsers = adminUsersData?.items || [];
  const notifications = notificationsData?.items || [];

  const stats: AdminDashboardStats = {
    customers: {
      total: customersData?.total || 0
    },
    licenses: {
      total: licensesData?.total || 0,
      active: licenses.filter((l) => l.active).length,
      inUse: licenses.filter((l) => l.inUse).length,
      available: licenses.filter((l) => l.active && !l.inUse).length
    },
    adminUsers: {
      total: adminUsersData?.total || 0
    },
    notifications: {
      total: notificationsData?.total || 0
    },
    newsChannels: {
      total: newsChannelsData?.total || 0
    },
    newsCategories: {
      total: newsCategoriesData?.total || 0
    },
    supportContacts: {
      total: supportContactsData?.total || 0
    }
  };

  const isLoading =
    isLoadingCustomers ||
    isLoadingLicenses ||
    isLoadingAdminUsers ||
    isLoadingNotifications ||
    isLoadingNewsChannels ||
    isLoadingNewsCategories ||
    isLoadingSupportContacts;

  // Recent items for quick access
  const recentCustomers = customers.slice(0, 5);
  const recentLicenses = licenses.slice(0, 5);
  const recentNotifications = notifications.slice(0, 5);

  return {
    stats,
    isLoading,
    recentCustomers,
    recentLicenses,
    recentNotifications,
    customers,
    licenses,
    adminUsers
  };
};

export default useControllerDashboardAdmin;
